// Solomon Portal — DOMPurify wrapper for rendering Help Scout thread bodies.
//
// Threat model
// ------------
// Help Scout stores conversation thread bodies as HTML (inbound emails
// arrive as multipart/alternative, HS picks the HTML part). Those
// bodies can contain anything an email-MUA composer can produce:
// arbitrary tags, inline styles, mailto:/http(s):/javascript: links,
// embedded images, even <script> if the upstream sender is malicious.
// We treat thread bodies as fully untrusted user-generated content
// even though the conversation is scoped to the authenticated portal
// user (a customer could have replied with HTML they wrote themselves
// or their email client crafted; we never want that markup executing
// in our origin).
//
// The Edge Function is the network trust boundary (it strips PII +
// internal-only tags), but it deliberately does NOT sanitize the
// thread bodies themselves — sanitization of HTML-as-rendered-markup
// is a render-time concern that needs a real DOM. Doing it on Deno
// would require pulling in a non-trivial dependency that drifts from
// the in-browser parser HS bodies will eventually be rendered against.
//
// Strategy
// --------
// DOMPurify with a strict allowlist suitable for an email-thread view:
// block-level structure, inline emphasis, links (forced to
// `target="_blank" rel="noopener noreferrer"`), images (display only),
// and lists. NO scripts, NO event handlers, NO iframes, NO forms, NO
// data:/javascript: URIs.
//
// We also strip Help Scout's email signature noise (`> On <date>, X
// wrote:` quoted-reply blocks) at the body level via a separate helper
// so the rendered bubble is the new content only. (Keeping this
// off-by-default for now; trivial to wire up when a thread has a long
// quoted-reply tail that adds noise.)
//
// Fallback
// --------
// If DOMPurify ever throws or returns an empty string for a non-empty
// input, callers fall back to a naive HTML-strip + line-break preserve
// rendered as plain text. The fallback is in `htmlToPlainText`.

// `isomorphic-dompurify` re-exports the standard DOMPurify API and
// auto-wires a jsdom window in Node-only environments (vitest happy-dom
// or jsdom, edge-function pre-render, SSR). In the browser it's a thin
// passthrough to the native `dompurify` package. Same API surface either
// way — we can use the static `DOMPurify.sanitize` / `addHook` / etc.
import DOMPurify from 'isomorphic-dompurify';

/**
 * Render-safe HTML string suitable for `dangerouslySetInnerHTML`.
 *
 * The branded type makes it impossible to accidentally pass an
 * un-sanitized string into the renderer — TypeScript won't let you.
 */
export type SafeHtml = string & { readonly __brand: 'SafeHtml' };

const ALLOWED_TAGS = [
  // Block / structure
  'p',
  'div',
  'span',
  'br',
  'hr',
  'blockquote',
  'pre',
  'code',
  // Headings (rare in email but harmless)
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  // Inline emphasis
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'sub',
  'sup',
  'mark',
  'small',
  // Lists
  'ul',
  'ol',
  'li',
  // Links + media (display-only)
  'a',
  'img',
  // Tables (Outlook & Gmail use them aggressively for layout)
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  // Definition lists (rare but harmless)
  'dl',
  'dt',
  'dd',
];

const ALLOWED_ATTRS = [
  // Links
  'href',
  'title',
  // Images
  'src',
  'alt',
  'width',
  'height',
  // Tables / cells (sizes only — color/background gets normalized by our
  // own theme below to avoid wild backgrounds breaking the bubble layout)
  'colspan',
  'rowspan',
  // Generic
  'dir',
  'lang',
];

/**
 * Pre-sanitize Help-Scout-specific structural noise that the user shouldn't
 * see in the conversation bubble:
 *
 *   1. The Help Scout email signature block
 *      (`<div class="helpscout-signature">…</div>` and Gmail's
 *      `class="gmail_signature"` variant). Removed entirely.
 *   2. The Outlook / Gmail quoted-reply tail (everything from
 *      `<blockquote class="gmail_quote">` onward, or a `<div
 *      class="OutlookMessageHeader">` separator).
 *   3. Empty paragraph-spacing artifacts (`<div><br></div>`,
 *      `<div>\s*</div>`, `<p>\s*</p>`) that HS emits in pairs after the
 *      visible body and would otherwise produce a tall blank tail.
 *
 * Runs BEFORE DOMPurify so the sanitizer's allowlist can't strip the
 * marker classes we need to find these regions. Idempotent.
 *
 * Exposed for unit tests; production callers go through `sanitizeThreadHtml`.
 */
export function stripHelpScoutSignatureNoise(rawHtml: string): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return rawHtml;
  let out = rawHtml;

  // 1. Help Scout / Gmail signature blocks. Use a non-greedy match
  //    against a balanced-ish <div>…</div> region; HS signatures are
  //    self-contained so the worst-case overrun is dropping the rest of
  //    the body, which we explicitly DON'T want — so we cap each match
  //    by anchoring on the opening tag and consuming up to the matched
  //    closing </div> at the same nesting depth via a simple counter.
  // Match `helpscout-signature`, `helpscout_signature`,
  // `gmail-signature`, `gmail_signature`, `helpscoutsignature`, etc.
  // Also tolerate the case where the marker class is one of several
  // space-separated classes (e.g. `class="foo helpscout-signature bar"`).
  out = stripBalancedTagBlock(
    out,
    /<div\b[^>]*\bclass="[^"]*(?:helpscout|gmail)[-_]?signature[^"]*"[^>]*>/i,
    'div',
  );

  // 2. Quoted-reply tails. `<blockquote class="gmail_quote">` is the
  //    canonical marker for Gmail; Outlook uses `<div
  //    class="OutlookMessageHeader">`. Remove everything from the
  //    marker through end-of-input — the user doesn't need to see what
  //    they themselves quoted.
  out = out.replace(/<blockquote\b[^>]*\bclass="[^"]*\bgmail_quote\b[^"]*"[\s\S]*$/i, '');
  out = out.replace(/<div\b[^>]*\bclass="[^"]*\bOutlookMessageHeader\b[^"]*"[\s\S]*$/i, '');

  // 3. Empty paragraph-spacing artifacts. `<div><br></div>` is HS's
  //    way of writing a blank line; it normally renders as a paragraph
  //    break, but in long sequences (after the body, before the
  //    signature we just stripped) it creates a tall empty tail.
  //    Collapse runs of ≥3 into 1.
  out = out.replace(/(?:<div>\s*<br\s*\/?>\s*<\/div>\s*){3,}/gi, '<div><br></div>');
  out = out.replace(/(?:<p>\s*<br\s*\/?>\s*<\/p>\s*){3,}/gi, '<p><br></p>');
  // Trailing empty divs/p after content end (post-signature-strip cleanup)
  out = out.replace(/(?:<div>\s*(?:<br\s*\/?>)?\s*<\/div>\s*)+$/i, '');
  out = out.replace(/(?:<p>\s*(?:<br\s*\/?>)?\s*<\/p>\s*)+$/i, '');

  return out.trim();
}

/**
 * Strip a balanced `<tag…>…</tag>` region whose opening tag matches
 * `openTagPattern`. Walks the input once, counting same-tag opens/closes
 * to find the matching close. Returns the input minus that region (or
 * unchanged if no match). Used by `stripHelpScoutSignatureNoise` for
 * signature blocks where naive non-greedy regex would over-strip.
 */
function stripBalancedTagBlock(input: string, openTagPattern: RegExp, tagName: string): string {
  const m = openTagPattern.exec(input);
  if (!m) return input;
  const start = m.index;
  const afterOpen = start + m[0].length;
  const openRe = new RegExp(`<${tagName}\\b[^>]*>`, 'gi');
  const closeRe = new RegExp(`<\\/${tagName}\\s*>`, 'gi');
  openRe.lastIndex = afterOpen;
  closeRe.lastIndex = afterOpen;
  let depth = 1;
  while (depth > 0) {
    const nextOpen = openRe.exec(input);
    const nextClose = closeRe.exec(input);
    if (!nextClose) {
      // No matching close — strip from `start` to end of input as a
      // safety fallback (the signature is structurally broken; better to
      // drop the tail than to leave stray markup).
      return input.slice(0, start).trim();
    }
    if (nextOpen && nextOpen.index < nextClose.index) {
      depth += 1;
      closeRe.lastIndex = nextOpen.index + nextOpen[0].length;
      continue;
    }
    depth -= 1;
    if (depth === 0) {
      const end = nextClose.index + nextClose[0].length;
      return (input.slice(0, start) + input.slice(end)).trim();
    }
    openRe.lastIndex = nextClose.index + nextClose[0].length;
  }
  return input;
}

/**
 * Sanitize a Help Scout thread body for safe rendering inside a
 * conversation bubble. Returns a branded `SafeHtml` string.
 *
 * Pipeline:
 *   1. `stripHelpScoutSignatureNoise` removes signature blocks +
 *      quoted-reply tails + empty paragraph runs (structural noise the
 *      user doesn't need to see).
 *   2. DOMPurify with a strict allowlist + forbid list (security).
 *   3. `<a>`-tag hardening: `target="_blank"` + `rel="noopener noreferrer"`.
 *
 * Behavioural guarantees:
 *   - All `<script>` / `<style>` / `<iframe>` / `<object>` / `<embed>`
 *     and any tag not on the allowlist is removed.
 *   - `on*` event-handler attributes are stripped.
 *   - `javascript:` and `data:` URIs in `href` / `src` are stripped.
 *   - Empty / whitespace-only input returns an empty SafeHtml string.
 */
export function sanitizeThreadHtml(rawHtml: string): SafeHtml {
  if (typeof rawHtml !== 'string' || rawHtml.trim().length === 0) {
    return '' as SafeHtml;
  }

  // Pipeline step 1: structural-noise strip (signature, quoted reply,
  // empty-paragraph runs). Runs BEFORE DOMPurify because we need the
  // marker classes (`helpscout-signature`, `gmail_quote`, etc.) which
  // the sanitizer's `class` strip would otherwise remove.
  const preCleaned = stripHelpScoutSignatureNoise(rawHtml);

  // Configure once per call; DOMPurify is stateless w.r.t. config and
  // re-running addHook is idempotent for the same fn reference.
  const linkHook = (node: Element): void => {
    if (node?.nodeName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  };
  DOMPurify.removeAllHooks();
  DOMPurify.addHook('afterSanitizeAttributes', linkHook);

  const cleaned = DOMPurify.sanitize(preCleaned, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ALLOWED_ATTRS,
    // Defense in depth even though the allowlist above already rejects
    // these — explicit FORBID lists make the contract obvious to anyone
    // reading the config and protect against any future allowlist edit
    // accidentally re-admitting a dangerous tag.
    FORBID_TAGS: [
      'script',
      'style',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'button',
      'textarea',
      'select',
      'option',
      'svg',
      'math',
      'base',
      'meta',
      'link',
    ],
    // Style + class attributes are stripped: inline CSS is an XSS surface
    // (`style="background:url(javascript:...)"`, `expression()` legacy,
    // CSS-injected layout breakage). Class is stripped because the
    // bubble's own theme styles MUST win over upstream classes.
    FORBID_ATTR: ['style', 'class', 'id', 'srcset', 'background', 'formaction'],
    // Defense in depth: keep DOMPurify's default URI allowlist, which
    // already rejects javascript:, data:, vbscript:, etc.
    ALLOW_DATA_ATTR: false,
    // Keep relative + http/https + mailto: links.
    KEEP_CONTENT: true,
    // Don't allow `<svg>` / `<math>` — both have well-known XSS
    // surfaces and are not needed for an email thread view.
    USE_PROFILES: { html: true },
  });

  DOMPurify.removeAllHooks();

  return cleaned as SafeHtml;
}

/**
 * Naive HTML → plain-text fallback. Used when DOMPurify is unavailable
 * (e.g. SSR contexts, test environments without a DOM, or a cataclysmic
 * sanitizer failure). Preserves visual line breaks from `<br>` and
 * paragraph boundaries, strips everything else.
 *
 * NOT a security boundary — for SSR/fallback rendering only. Real
 * sanitization happens in `sanitizeThreadHtml`.
 */
export function htmlToPlainText(rawHtml: string): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return '';
  return (
    rawHtml
      // <br> + </p> + </div> + </li> => newline
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
      // strip the rest
      .replace(/<[^>]+>/g, '')
      // decode the most common entities (full decode would need a DOM)
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // collapse 3+ newlines to 2 (paragraph break)
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}
